import { convertFileListToFileUIParts } from 'ai';
import type { FileUIPart, UIMessage } from 'ai';
import { useState } from 'react';

import { Icon, Text, useToast } from '@eluve/components';

// Attachments for the Plan Builder composer: reference material the agent folds
// into the draft — a photo of a program sheet, a whiteboard sketch, a
// screenshot of a legacy plan, or a PDF document (e.g. a clinical guideline).

// Claude vision's supported image set — keep in lock-step with the server-side
// schema in the planBuilder SSE contract.
export const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;
// Claude reads PDFs natively (same as the document-extraction agent). Clean,
// text-based PDFs only — scanned/encrypted are out of scope.
export const ACCEPTED_DOCUMENT_TYPES = ['application/pdf'] as const;
export const ACCEPTED_TYPES = [
  ...ACCEPTED_IMAGE_TYPES,
  ...ACCEPTED_DOCUMENT_TYPES,
] as const;

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // Anthropic's per-image limit
export const MAX_PDF_BYTES = 10 * 1024 * 1024; // inline base64 cap for PDFs
export const MAX_ATTACHMENTS_PER_MESSAGE = 5; // combined images + documents
// Combined data-URL payload ceiling, held under the 25mb API body limit so a
// batch that passes client validation never trips the Express json parser
// (which 413s before the request reaches the controller). A 10MB raw file is
// ~14MB once base64-encoded, so the per-file caps alone don't bound the total.
export const MAX_TOTAL_ATTACHMENT_CHARS = 22 * 1024 * 1024;

export type AcceptedType = (typeof ACCEPTED_TYPES)[number];

function isAcceptedImageType(type: string): boolean {
  return (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(type);
}

function isPdf(type: string): boolean {
  return type === 'application/pdf';
}

function isAcceptedType(type: string): type is AcceptedType {
  return (ACCEPTED_TYPES as readonly string[]).includes(type);
}

// Approximate the length of a file's base64 data URL (4 chars per 3 bytes,
// plus the `data:<mime>;base64,` prefix) — an upper bound, so the aggregate
// guard stays conservative against the body limit.
function estimateEncodedChars(file: File): number {
  return Math.ceil(file.size / 3) * 4 + 64;
}

// The attachment (image + PDF) file parts of a message — for message views.
export function attachmentFileParts(message: {
  parts: UIMessage['parts'];
}): FileUIPart[] {
  return message.parts.filter(
    (p): p is FileUIPart => p.type === 'file' && isAcceptedType(p.mediaType),
  );
}

// The same parts in the wire shape the SSE endpoint takes — narrowed to the
// accepted media types and stripped of part-only fields.
export function outgoingFiles(message: { parts: UIMessage['parts'] }): Array<{
  mediaType: AcceptedType;
  filename?: string;
  url: string;
}> {
  return attachmentFileParts(message).flatMap((p) =>
    isAcceptedType(p.mediaType)
      ? [{ mediaType: p.mediaType, filename: p.filename, url: p.url }]
      : [],
  );
}

// Pending-attachment state: validates (type / size / count, with toast
// feedback) and converts accepted files to data-URL file parts ready for
// `sendMessage({ text, files })`.
export function useAttachments() {
  const [attachments, setAttachments] = useState<FileUIPart[]>([]);
  const toast = useToast();

  const addFiles = async (incoming: ArrayLike<File> | null) => {
    const files = Array.from(incoming ?? []);
    if (files.length === 0) {
      return;
    }

    const errors: string[] = [];
    let accepted = files.filter((file) => {
      if (!isAcceptedType(file.type)) {
        errors.push(
          `${file.name}: only JPEG, PNG, GIF, or WebP images, or PDF documents are supported.`,
        );
        return false;
      }
      const maxBytes = isPdf(file.type) ? MAX_PDF_BYTES : MAX_IMAGE_BYTES;
      if (file.size > maxBytes) {
        errors.push(
          isPdf(file.type)
            ? `${file.name}: PDFs must be 10MB or smaller.`
            : `${file.name}: images must be 5MB or smaller.`,
        );
        return false;
      }
      return true;
    });

    const room = MAX_ATTACHMENTS_PER_MESSAGE - attachments.length;
    if (accepted.length > room) {
      errors.push(
        `Up to ${MAX_ATTACHMENTS_PER_MESSAGE} attachments per message.`,
      );
      accepted = accepted.slice(0, Math.max(0, room));
    }

    // Bound the combined payload so a batch that passes per-file/count checks
    // can't still exceed the API body limit. Admit greedily so the first files
    // selected still attach; reject the overflow with a toast.
    let usedChars = attachments.reduce((sum, a) => sum + a.url.length, 0);
    accepted = accepted.filter((file) => {
      const size = estimateEncodedChars(file);
      if (usedChars + size > MAX_TOTAL_ATTACHMENT_CHARS) {
        errors.push(
          `${file.name}: not enough room — attachments must total under 22MB.`,
        );
        return false;
      }
      usedChars += size;
      return true;
    });

    if (accepted.length > 0) {
      // The AI SDK's own FileList → data-URL file-part conversion. It only
      // takes a real FileList, so re-wrap the validated files in one.
      const transfer = new DataTransfer();
      for (const file of accepted) {
        transfer.items.add(file);
      }
      const parts = await convertFileListToFileUIParts(transfer.files);
      // The updater is the authoritative gate: two adds racing (e.g. paste
      // then drop) both read the same stale `attachments`/`usedChars` above,
      // so the count AND the combined-payload cap must be re-enforced here
      // against the latest state, using the real encoded `url` lengths.
      setAttachments((prev) => {
        let used = prev.reduce((sum, a) => sum + a.url.length, 0);
        const admitted: FileUIPart[] = [];
        for (const part of parts) {
          if (prev.length + admitted.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
            break;
          }
          if (used + part.url.length > MAX_TOTAL_ATTACHMENT_CHARS) {
            continue;
          }
          used += part.url.length;
          admitted.push(part);
        }
        return admitted.length > 0 ? [...prev, ...admitted] : prev;
      });
    }
    if (errors.length > 0) {
      toast.error(errors.join('\n'));
    }
  };

  return {
    attachments,
    addFiles,
    removeAt: (index: number) =>
      setAttachments((prev) => prev.filter((_, i) => i !== index)),
    clear: () => setAttachments([]),
  };
}

// One not-yet-sent attachment: an image thumbnail, or a labelled chip for a PDF
// (which can't render as an <img>), each with an ✕ to remove.
function AttachmentTile({
  attachment,
  onRemove,
}: {
  attachment: FileUIPart;
  onRemove: () => void;
}) {
  const removeButton = (
    <button
      type="button"
      aria-label={`Remove ${attachment.filename ?? 'attachment'}`}
      onClick={onRemove}
      className="absolute -top-1.5 -right-1.5 grid size-5 place-items-center rounded-full border border-border bg-background text-muted-foreground shadow-sm transition-colors hover:text-foreground"
    >
      <Icon name="X" size="auto" className="size-3" />
    </button>
  );

  if (isAcceptedImageType(attachment.mediaType)) {
    return (
      <div className="relative">
        <img
          src={attachment.url}
          alt={attachment.filename ?? 'attached image'}
          className="size-16 rounded-lg border border-border object-cover shadow-sm"
        />
        {removeButton}
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        className="flex h-16 w-40 items-center gap-2 rounded-lg border border-border bg-card px-3 shadow-sm"
        title={attachment.filename}
      >
        <Icon
          name="FileText"
          size="auto"
          className="size-6 shrink-0 text-muted-foreground"
        />
        <Text size="xs" className="truncate">
          {attachment.filename ?? 'document.pdf'}
        </Text>
      </div>
      {removeButton}
    </div>
  );
}

// Thumbnails / chips for not-yet-sent attachments.
export function AttachmentStrip({
  attachments,
  onRemove,
}: {
  attachments: FileUIPart[];
  onRemove: (index: number) => void;
}) {
  if (attachments.length === 0) {
    return null;
  }
  return (
    <div className="mb-2 flex flex-wrap gap-2">
      {attachments.map((a, i) => (
        <AttachmentTile
          key={`${a.filename ?? 'attachment'}-${i}`}
          attachment={a}
          onRemove={() => onRemove(i)}
        />
      ))}
    </div>
  );
}
